import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  TableComponent,
  TableConfig,
  PaginatorFetcher,
  PaginatorFilters,
  ModalComponent,
  ModalResult,
  AlertType,
  Alert,
  AlertService
} from '@michelin/theme';
import { FormBuilder, FormGroup, FormArray, FormControl } from '@angular/forms';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import { FinishedProduct } from '../../models/finished-product.model';
import { map, Observable, from, of, throwError } from 'rxjs';
import { ProductDataService } from '../../services/product-data.service';
import { ProductsState } from '../../../../shared/state/ProductsState';
import { Store } from '@ngxs/store';
import { distinctUntilChanged, debounceTime, concatMap, mergeMap, toArray, timeout, catchError } from 'rxjs/operators';
import { PaginatedFinishedProductInfo, PaginatedFinishedProductResponse, Pagination } from '../../models/response.model';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { Plant } from '../../../plant-setup/model/Plant';

import { MatTableDataSource } from '@angular/material/table';
import { AssignToPlantsFinishedProduct } from '../../models/AssignToPlantsFinishedProduct';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { hasRolePermission } from '../../../user-management/utils/UserSetupUtils';
import { ROLES } from '../../../authentication/authRoles.constants';
import { ExportResponse } from '../../../../shared/services/export-to-excel.service';
import { MatDialog } from '@angular/material/dialog';
import { ExportModalComponent, ExportModalData } from '../../../../shared/components/export-modal/export-modal.component';
import { ExportColumnDef } from '../../../../shared/models/export-column-def.model';
import { environment } from '../../../../../environments/environment';
import { DealerState } from '../../../../shared/state/DealerState';
import { ColumnPreferencesService } from '../../../../shared/services/column-preferences.service';
import { ColumnPreferencesHelperService } from '../../../../shared/services/column-preferences-helper.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-finished-product-screen',
  templateUrl: './finished-product-screen.component.html',
  styleUrls: ['./finished-product-screen.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FinishedProductScreenComponent implements OnInit, OnDestroy {
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<FinishedProduct>;
  @ViewChild('viewAssignPlant', { static: true }) viewAssignedPlant: ModalComponent;
  @ViewChild('assignedToPlant') assignedtoplantContent: TemplateRef<any>;
  @ViewChild('discardConfirmation', { static: true }) discardModal: ModalComponent;
  @ViewChild('discardContent') discardContent: TemplateRef<any>;

  public fb = inject(FormBuilder);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public featureFlagsService = inject(FeatureflagsService);
  public alertService = inject(AlertService);
  public cdr = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);
  public columnPrefService = inject(ColumnPreferencesService);
  public columnPrefsHelper = inject(ColumnPreferencesHelperService);

  isExporting = false;
  public isProduction = environment.production;
  dealerName = this.store.selectSnapshot(DealerState.currentDealer).name;
  // Export tuning constants: increase pageSize to reduce number of requests and increase concurrency
  private readonly EXPORT_PAGE_SIZE = 400; // server must support this; adjust as needed
  private readonly EXPORT_CONCURRENCY = 6; // number of parallel page requests
  private dialog = inject(MatDialog);

  // Dropdown/filter lists
  michelinStatusList: string[] = [];
  plantAssignmentList: string[] = [];
  cureTypeList: string[] = [];
  treadList: string[] = [];
  treadWidthList: string[] = [];
  sizeList: string[] = [];
  selectedFinishedProducts: string[];
  filterDropdownWidth = '200px';

  // Table dropdown lists

  // Shared, stable "empty" reference bound to every dropdown's [data] by default. Reusing the
  // same array reference (rather than a fresh []) avoids extra change-detection churn.
  readonly dealerRetreadNoEmptyList: string[] = [];
  dealerRetreadNoList: string[] = [];
  // Identifies exactly which cell (row + field) most recently had its dropdown clicked/opened.
  // Only that single cell is bound to the real (potentially large) dealerRetreadNoList; every
  // other cell keeps the shared empty array. This is what actually keeps memory bounded at
  // larger page sizes: without per-cell tracking, populating a single shared list property (as
  // in the previous version of getData()) ends up feeding ALL ~700 dropdown instances at once
  // the moment any one of them is clicked, since they all read the same property reference.
  activeDropdownCell: { pk: string; field: string } | null = null;
  // Cached width config for the dropdown columns, computed once whenever dealerRetreadNoList
  // changes (see updateDropdownLists) instead of via a template-bound getter. dealerRetreadNoList
  // can hold thousands of entries, and this value is read by all 7 dropdown columns on every
  // visible row, so recomputing it on every change-detection cycle (as a getter would) is O(n)
  // work repeated dozens/hundreds of times per digest — a major source of page slowness.
  private cachedDropdownWidthConfig: { width: string } = { width: '150px' };

  filterForm: FormGroup;
  filterFormBackend: FormGroup;
  isAssignDisabled = true;
  productSaveDisabled = true;
  saveEnabled = false;
  isModified = false;
  finishedProducts: FinishedProduct[] = [];
  assignToPlants: AssignToPlantsFinishedProduct;
  plants: Plant[] = [];
  plantsSelected: Plant[] = [];
  plantDataSource = new MatTableDataSource();
  displayedRowsForm: FormGroup = this.fb.group({ rows: this.fb.array([]) });
  // Maps finishedProductPk -> row index in `finishedProducts` / `displayedRowsForm` rows FormArray.
  // Rebuilt once per fetch (see updateDisplayedRowsForm), so getControl() becomes an O(1) lookup
  // instead of a linear findIndex scan repeated on every change-detection cycle for every one of
  // the 7 dropdown columns on every visible row.
  private rowIndexByPk = new Map<string, number>();
  pagination: Pagination;
  pageSizeOptions = [5, 10, 50, 100];
  currentPage = 1;
  pageSize = 10;

  tableConfig: TableConfig<FinishedProduct> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    selection: {
      columnKeyForCheckboxLabel: 'name',
      allLabel: 'All'
    },
    onSelectionChange: (elms) => {
      this.isAssignDisabled = elms.length == 0;
      this.selectedFinishedProducts = [];
      elms.forEach((elm) => {
        if (!this.selectedFinishedProducts.includes(elm.finishedProductPk)) {
          this.selectedFinishedProducts.push(elm.finishedProductPk);
        }
      });
    },
    columns: {}
  };

  tableConfig2: TableConfig<Plant> = {
    onSelectionChange: (elms: Plant[]) => {
      this.plantsSelected = elms;
    },
    onRowClick: (elm: Plant) => {
      console.log('Row clicked', elm);
    },
    selection: {
      columnKeyForCheckboxLabel: 'name',
      allLabel: 'All'
    },
    onColumnsChange: (columns: string[]) => console.log('Column display change', columns),
    columns: {
      code: {
        cell: (elm: Plant) => elm.code,
        header: this.translationService.translate('plantCodeText'),
        sortable: true
      },
      name: {
        cell: (elm: Plant) => elm.name,
        header: this.translationService.translate('plantNameText'),
        sortable: false
      }
    }
  };

  columnNames: Record<string, string> = {};
  private canonicalColumns: Record<string, any> = {};
  columnKeys: string[] = [];
  columnOrder: string[] = [];
  columnVisibility: Record<string, boolean> = {};

  // Accept selectedColumns so the export can request only the needed fields from the backend
  exportDataFetcher: (selectedColumns?: string[]) => Observable<ExportResponse> = (selectedColumns) => {
    const pageSize = this.EXPORT_PAGE_SIZE;
    const filters = this.filterFormBackend.value;

    // If caller didn't provide selectedColumns, fallback to all available column keys
    const columnsToRequest = selectedColumns && selectedColumns.length > 0 ? selectedColumns : Object.keys(this.columnNames || {});

    return this.productDataService.getFinishedProductLines({ ...filters }, 1, pageSize, columnsToRequest).pipe(
      concatMap((firstResp: any) => {
        const total = firstResp?.data?.pagination?.totalRecords ?? ((firstResp.data.finishedProducts || []).length || 0);
        const cappedTotal = Math.min(total, 10000);
        const totalPages = Math.max(1, Math.ceil(cappedTotal / pageSize));

        const pageObservables = [] as Observable<any>[];
        pageObservables.push(of(firstResp));
        for (let p = 2; p <= totalPages; p++) {
          pageObservables.push(this.productDataService.getFinishedProductLines(filters, p, pageSize, columnsToRequest));
        }

        return from(pageObservables).pipe(
          mergeMap((obs) => obs, this.EXPORT_CONCURRENCY),
          map((r: any) => (r?.data?.finishedProducts ?? []) as FinishedProduct[]),
          toArray(),
          map((chunks: FinishedProduct[][]) => ([] as FinishedProduct[]).concat(...chunks)),
          map((all: FinishedProduct[]) => {
            const rows = all.slice(0, cappedTotal).map((item) => {
              // Build a reduced object containing only the requested keys
              const reduced: any = {};
              (columnsToRequest || []).forEach((key) => {
                reduced[key] = (item as any)[key];
              });
              return reduced;
            });
            return { data: rows } as ExportResponse;
          })
        );
      }),
      timeout(300000),
      catchError((err) => {
        if (err && err.name === 'TimeoutError') {
          console.error('Export timed out after 30 seconds', err);
          return throwError(() => new Error('Export timed out after 30 seconds'));
        }
        console.error('Export failed', err);
        return throwError(() => err);
      })
    );
  };

  fetcher: PaginatorFetcher<FinishedProduct> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    this.activeDropdownCell = null;
    this.generateFilterForm();
    return this.productDataService
      .getFinishedProductLines({ ...this.filterFormBackend.value, ...filters }, filters.page, filters.limit)
      .pipe(
        map((response: PaginatedFinishedProductResponse) => {
          this.finishedProducts = response.data.finishedProducts || [];
          this.plants = response.data.plants || [];
          this.plantDataSource = new MatTableDataSource(this.plants);
          this.plantAssignmentList = ['All Plants', 'No Plants', ...this.plants.map((plant) => plant.code)];
          this.pagination = response.data.pagination;
          this.updateDisplayedRowsForm(this.finishedProducts);
          return { items: this.finishedProducts, count: this.pagination.totalRecords };
        })
      );
  };

  constructor() {
    this.treadList = this.store.selectSnapshot(ProductsState.products).allTreads;
    this.treadWidthList = this.store.selectSnapshot(ProductsState.products).widths;
    this.sizeList = this.store.selectSnapshot(ProductsState.products).sizes;
    this.filterForm = this.fb.group({
      michelinStatus: [[]],
      plantAssignment: [[]],
      cureType: [[]],
      treads: [[]],
      treadWidth: [[]],
      size: [[]]
    });
    this.filterFormBackend = this.fb.group({
      michelinStatus: [[]],
      plantAssignment: [[]],
      cureType: [[]],
      treads: [[]],
      treadWidth: [[]],
      size: [[]]
    });
  }

  ngOnInit(): void {
    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    this.saveEnabled = !this.productSaveDisabled && hasRolePermission(ROLES.edit_product);
    if (this.productSaveDisabled) {
      // this.utilityService.updateSharedData("top right"); -- Example on how to move alerts up
      this.alertService.showAlert({
        type: AlertType.classical,
        title: this.translationService.translate('readOnlyProductDataHeader'),
        content: this.translationService.translate('readOnlyProductDataText'),
        timestamp: 60000 // 60 seconds
      } as Alert);
    }
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      // build canonical columns (used as source of truth) and apply saved preferences
      this.canonicalColumns = {
        tread: { cell: (row) => row.tread, header: this.translationService.translate('treadText') },
        treadWidth: { cell: (row) => row.treadWidth, header: this.translationService.translate('treadWidthText') },
        size: { cell: (row) => row.size, header: this.translationService.translate('sizeText') },
        mspn: { cell: (row) => row.mspn, header: this.translationService.translate('mspnText') },
        dealerRetreadNo: {
          cell: 'dealerRetreadNoDropdown',
          header: this.translationService.translate('dealerRetreadNoText')
        },
        dealerCapNo: { cell: 'dealerCapNoDropdown', header: this.translationService.translate('dealerCapNoText') },
        dealerCasingNo: {
          cell: 'dealerCasingNoDropdown',
          header: this.translationService.translate('dealerCasingNoText')
        },
        dealerCapAndCasingNo: {
          cell: 'dealerCapAndCasingNoDropdown',
          header: this.translationService.translate('dealerCapAndCasingNoText')
        },
        dealerAdRetreadNo: {
          cell: 'dealerAdRetreadNoDropdown',
          header: this.translationService.translate('dealerAdRetreadNoText')
        },
        dealerAdCapNo: { cell: 'dealerAdCapNoDropdown', header: this.translationService.translate('dealerAdCapNoText') },
        dealerAdCapAndCasingNo: {
          cell: 'dealerAdCapAndCasingNoDropdown',
          header: this.translationService.translate('dealerAdCasingNoText')
        },
        michelinStatus: {
          cell: (row) => row.michelinStatus,
          header: this.translationService.translate('michelinStatusText')
          // display: false
        },
        plantAssignment: {
          cell: (row) => row.plantAssignment,
          header: this.translationService.translate('plantAssignmentColumnText')
        },
        zoneStockNo: {
          cell: (row) => row.zoneStockNo,
          header: this.translationService.translate('zoneStockNoText')
          // display: false
        },
        cureType: {
          cell: (row) => row.cureType,
          header: this.translationService.translate('cureTypeText')
          // display: false
        }
      };

      this.columnNames = {
        tread: this.translationService.translate('treadText'),
        treadWidth: this.translationService.translate('treadWidthText'),
        size: this.translationService.translate('sizeText'),
        mspn: this.translationService.translate('mspnText'),
        dealerRetreadNo: this.translationService.translate('dealerRetreadNoText'),
        dealerCapNo: this.translationService.translate('dealerCapNoText'),
        dealerCasingNo: this.translationService.translate('dealerCasingNoText'),
        dealerCapAndCasingNo: this.translationService.translate('dealerCapAndCasingNoText'),
        dealerAdRetreadNo: this.translationService.translate('dealerAdRetreadNoText'),
        dealerAdCapNo: this.translationService.translate('dealerAdCapNoText'),
        dealerAdCapAndCasingNo: this.translationService.translate('dealerAdCasingNoText'),
        michelinStatus: this.translationService.translate('michelinStatusText'),
        plantAssignment: this.translationService.translate('plantAssignmentColumnText'),
        zoneStockNo: this.translationService.translate('zoneStockNoText'),
        cureType: this.translationService.translate('cureTypeText')
      };

      // apply user preferences (localStorage) and initialize tableConfig.columns
      const init = this.columnPrefsHelper.initialize('finished-product', this.canonicalColumns);
      this.tableConfig.columns = init.tableColumns;
      this.columnNames = init.columnNames;
      this.columnKeys = init.columnKeys;
      this.columnOrder = init.columnOrder;
      this.columnVisibility = init.columnVisibility;

      this.cdr.markForCheck();
      this.computeFilterDropdownWidth();
    });
    this.filterForm.valueChanges
      .pipe(
        // Only emit when the value actually changes
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        debounceTime(800)
      )
      .subscribe(() => {
        this.generateFilterForm();
        this.tableRef?.patchExtraFilters(this.filterFormBackend.value, false);
      });
    this.loadDropdownOptions();
    this.updateDropdownLists();
    this.computeFilterDropdownWidth();
    this.displayedRowsForm.valueChanges.subscribe(() => {
      this.isModified = this.displayedRowsForm.dirty;
    });
    // Register capture-phase Tab interceptor so we run BEFORE Angular Material's
    // CDK overlay keyboard handler, which would otherwise steal Tab and focus the
    // option list instead of moving to the next table field.
    document.addEventListener('keydown', this.tabCaptureHandler, true);
  }

  ngOnDestroy(): void {
    document.removeEventListener('keydown', this.tabCaptureHandler, true);
  }

  loadDropdownOptions(): void {
    this.michelinStatusList = ['Active', 'Inactive'];
    this.plantAssignmentList = ['All Plants', 'No Plants', ...this.plants.map((plant) => plant.code)];
    this.cureTypeList = ['Pre Mold', 'Custom Mold'];
  }

  private computeFilterDropdownWidth(): void {
    const t = (key: string) => this.translationService.translate(key);
    const placeholders = [
      t('searchByText') + ' ' + t('michelinStatusText'),
      'Select ' + t('plantAssignmentColumnText'),
      'Select ' + t('cureTypeText'),
      t('selectTreadText'),
      t('selectTreadWidthText'),
      t('selectSizeText')
    ];
    const maxLen = placeholders.reduce((max, p) => Math.max(max, p.length), 0);
    const widthPx = Math.max(200, maxLen * 8 + 56); // ~8px per char + 56px for padding & icon
    this.filterDropdownWidth = `${widthPx}px`;
    this.cdr.markForCheck();
  }

  updateDropdownLists(): void {
    this.dealerRetreadNoList.push(...this.store.selectSnapshot(ProductsState.products).finishedPartNumbers);
    this.recomputeDropdownWidthConfig();
  }

  private recomputeDropdownWidthConfig(): void {
    const maxLen = this.dealerRetreadNoList.reduce((max, val) => Math.max(max, (val ?? '').length), 0);
    const widthPx = Math.max(150, maxLen * 9 + 48); // ~9px per char + 48px for padding & icon
    this.cachedDropdownWidthConfig = { width: `${widthPx}px` };
  }

  // Toggle visibility for a column and persist preferences (delegates to helper)
  toggleColumn(key: string): void {
    const result = this.columnPrefsHelper.toggleAndSave(
      'finished-product',
      key,
      this.canonicalColumns,
      this.columnOrder,
      this.columnVisibility
    );
    this.tableConfig = {
      ...this.tableConfig,
      columns: result.tableColumns
    };
    this.columnVisibility = result.columnVisibility;
    this.cdr.markForCheck();
  }

  onColumnDrop(event: CdkDragDrop<string[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    moveItemInArray(this.columnOrder, event.previousIndex, event.currentIndex);

    const columns: Record<string, any> = {};
    this.columnOrder.forEach((key) => {
      if (this.columnVisibility[key]) {
        columns[key] = this.canonicalColumns[key];
      }
    });

    this.tableConfig = {
      ...this.tableConfig,
      columns
    };
    this.columnPrefsHelper.save('finished-product', this.columnOrder, this.columnVisibility, this.canonicalColumns);
    this.cdr.markForCheck();
  }

  get dropdownWidthConfig(): { width: string } {
    return this.cachedDropdownWidthConfig;
  }

  createRowForm(row: FinishedProduct): FormGroup {
    return this.fb.group({
      dealerRetreadNo: [row.dealerRetreadNo],
      dealerCapNo: [row.dealerCapNo],
      dealerCasingNo: [row.dealerCasingNo],
      dealerCapAndCasingNo: [row.dealerCapAndCasingNo],
      dealerAdRetreadNo: [row.dealerAdRetreadNo],
      dealerAdCapNo: [row.dealerAdCapNo],
      dealerAdCasingNo: [row.dealerAdCasingNo]
    });
  }

  private updateDisplayedRowsForm(rows: FinishedProduct[]) {
    const rowForms = rows.map((row) => this.createRowForm(row));
    const formArray = this.fb.array(rowForms);
    this.displayedRowsForm.setControl('rows', formArray);
    this.rowIndexByPk = new Map(rows.map((row, index) => [row.finishedProductPk, index]));
  }

  getControl(item: FinishedProduct, controlName: keyof FinishedProduct): FormControl {
    const index = this.rowIndexByPk.get(item.finishedProductPk);
    if (index === undefined) {
      return new FormControl('');
    }
    return (this.displayedRowsForm.get('rows') as FormArray).at(index).get(controlName) as FormControl;
  }

  clearFilters(): void {
    this.filterForm.reset({
      michelinStatus: [],
      plantAssignment: [],
      cureType: [],
      treads: [],
      treadWidth: [],
      size: []
    });
    this.selectedFinishedProducts = [];
  }

  save(): void {
    if (!this.saveEnabled) {
      return;
    }
    const modifiedRows = [];
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    for (let i = 0; i < this.finishedProducts.length; i++) {
      const original = this.finishedProducts[i];
      const current = formArray.at(i).value;
      // Compare original and current row (shallow compare all fields), treating null/undefined/"" as equivalent
      const isModified = this.isRowModified(original, current);
      if (isModified) {
        modifiedRows.push({ ...current, finishedProductPk: original.finishedProductPk });
      }
    }
    if (modifiedRows.length > 0) {
      const payload: PaginatedFinishedProductInfo = {
        finishedProducts: modifiedRows,
        plants: this.plants,
        pagination: this.pagination
      };
      this.productDataService.updateFinishedProducts(payload).subscribe({
        next: () => {
          this.alertService.showAlert({
            type: AlertType.success,
            content: this.translationService.translate('camSaveSuccessText')
          } as Alert);
          this.isModified = false;
        },
        error: () => {
          this.alertService.showAlert({
            type: AlertType.critical,
            content: this.translationService.translate('criticalErrorAlertText')
          } as Alert);
        }
      });
    } else {
      this.alertService.showAlert({
        type: AlertType.strong,
        content: this.translationService.translate('noModifiedText')
      } as Alert);
    }
  }

  generateFilterForm(): void {
    const filterTreads = this.filterForm.get('treads').value;
    const filterWidth = this.filterForm.get('treadWidth').value;
    const filterSize = this.filterForm.get('size').value;
    const cureType = this.filterForm.get('cureType').value;
    this.filterFormBackend.patchValue({
      michelinStatus: this.filterForm.get('michelinStatus').value,
      plantAssignment: this.filterForm.get('plantAssignment').value,
      cureType: cureType === 'Pre Mold' ? 'P' : cureType === 'Custom Mold' ? 'M' : cureType,
      treads: filterTreads.length > 0 && filterTreads.length == this.treadList.length ? [] : filterTreads,
      treadWidth: filterWidth.length > 0 && filterWidth.length == this.treadWidthList.length ? [] : filterWidth,
      size: filterSize.length > 0 && filterSize.length == this.sizeList.length ? [] : filterSize
    });
  }

  showAssignPlantModel() {
    this.viewAssignedPlant.open({
      id: 'assignedToPlant',
      width: 950,
      title: this.translationService.translate('selectPlantText'),
      content: this.assignedtoplantContent,
      confirm: {
        text: this.translationService.translate('applyText'),
        color: 'primary'
      },
      cancel: this.translationService.translate('cancelLinkText')
    });
  }

  onAssignPlantModalClosed(result: ModalResult) {
    if (result.result && !this.productSaveDisabled) {
      const allPlants = this.plantsSelected.length === this.plants.length; // simplified boolean assignment
      this.assignToPlants = {
        allPlants,
        plants: this.plantsSelected.map((plant) => plant.id),
        finishedProductsPk: this.selectedFinishedProducts
      };
      this.productDataService.assignPlantToFinishedProduct(this.assignToPlants).subscribe({
        next: (v) => {
          if (v) {
            this.alertService.showAlert({
              type: AlertType.success,
              content: this.translationService.translate('plantAssignmentSuccessText')
            } as Alert);
            this.tableRef.selectedItems = [];
            this.tableRef.selection.clear();
            this.tableRef.resetFilters(true);
            this.isAssignDisabled = true;
            this.cdr.detectChanges();
          }
        },
        error: (error) => {
          const statusCode: number = error.error.statusCode;
          const alertType = 400 <= statusCode && statusCode < 500 ? AlertType.strong : AlertType.critical;
          this.alertService.showAlert({
            type: alertType,
            content: this.translationService.translate('criticalErrorAlertText')
          } as Alert);
        }
      });
    }
  }

  showDiscardModal(): void {
    this.discardModal.open({
      id: 'discardConfirmation',
      title: this.translationService.translate('discardChangesText'),
      width: 550,
      content: this.discardContent,
      confirm: this.translationService.translate('okText'),
      cancel: this.translationService.translate('cancelText')
    });
  }

  openExportModal(): void {
    const columns: ExportColumnDef[] = Object.entries(this.columnNames).map(([key, label]) => ({ key, label, selected: true }));
    const data: ExportModalData = {
      columns,
      dataFetcher: this.exportDataFetcher,
      fileNamePrefix: this.dealerName + '-FinishedProduct-export',
      maxExportRows: 10000
    };
    this.dialog.open(ExportModalComponent, { data, width: '480px' });
  }

  onDiscardModalClosed(result: ModalResult): void {
    if (result) {
      if (result.id === 'discardConfirmation' && result.result) {
        this.updateDisplayedRowsForm(this.finishedProducts);
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
      }
    }
  }

  // --- Tab navigation + Ctrl-V paste for the finished-product table ----------

  // Shared overlay selectors (single source of truth)
  private static readonly SEL_OPTIONS =
    '.cdk-overlay-container .mat-mdc-select-panel mat-option:not([aria-disabled="true"]),' +
    '.cdk-overlay-container .mat-select-panel mat-option:not([aria-disabled="true"])';

  private static readonly SEL_SEARCH =
    '.cdk-overlay-container .mat-mdc-select-panel input[type="text"],' +
    '.cdk-overlay-container .mat-select-panel input[type="text"],' +
    '.cdk-overlay-container input[type="text"]';

  /**
   * Capture-phase Tab interceptor — registered with `addEventListener(..., true)` so it
   * runs BEFORE Angular Material's CDK overlay keyboard handler.
   *
   * When the dropdown panel is open and focus is in the overlay (search input), Angular
   * Material's bubble-phase handler intercepts Tab to move focus to the option list.
   * By running in capture phase and calling stopImmediatePropagation we prevent that
   * entirely, so a single Tab always commits (if search has text) or skips (if empty)
   * and moves directly to the next table field — no double-tab needed.
   */
  private readonly tabCaptureHandler = (event: KeyboardEvent): void => {
    if (event.key !== 'Tab') return;

    const active = document.activeElement as HTMLElement;
    if (!active?.closest('.cdk-overlay-container')) return;

    const open = this.openTableSelect();
    if (!open) return;

    // We own this Tab — prevent browser default AND stop Angular Material from seeing it
    event.preventDefault();
    event.stopImmediatePropagation();

    const dir = event.shiftKey ? (-1 as const) : (1 as const);
    const searchInput = document.querySelector<HTMLInputElement>(FinishedProductScreenComponent.SEL_SEARCH);
    const hasSearchText = !!searchInput?.value?.trim();

    if (hasSearchText) {
      const top = this.topPanelOption();
      if (top) {
        this.selectAndAdvance(top, open, dir);
        return;
      }
    }

    // No search text — cancel the panel with Escape so Angular Material reverts
    // to the original field value (or keeps it null/empty). Do NOT use blur()
    // because that can commit a highlighted option without user intent.
    active.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', keyCode: 27, bubbles: true, cancelable: true }));
    setTimeout(() => this.moveTableDropdownFocus(open, dir), 60);
  };

  /** All mic-dropdown-search mat-selects in the table in DOM order. */
  private getTableDropdowns(): HTMLElement[] {
    return Array.from(document.querySelectorAll<HTMLElement>('#finished-product-table mic-dropdown-search mat-select'));
  }

  /** Currently open table mat-select (aria-expanded="true"), or null. */
  private openTableSelect(): HTMLElement | null {
    return document.querySelector<HTMLElement>('#finished-product-table mic-dropdown-search mat-select[aria-expanded="true"]');
  }

  /**
   * Index of `el` in `list`; falls back to its mic-dropdown-search's mat-select
   * when `el` is a stale reference or a non-select child element.
   */
  private dropdownIdx(el: HTMLElement, list: HTMLElement[]): number {
    const direct = list.indexOf(el);
    if (direct !== -1) return direct;
    const ms = el.closest('mic-dropdown-search')?.querySelector<HTMLElement>('mat-select');
    return ms ? list.indexOf(ms) : -1;
  }

  /**
   * Topmost visible panel option by screen Y-position.
   * Uses getBoundingClientRect so virtual-scroll DOM ordering cannot mislead us.
   */
  private topPanelOption(): HTMLElement | null {
    const opts = Array.from(document.querySelectorAll<HTMLElement>(FinishedProductScreenComponent.SEL_OPTIONS));
    return opts.reduce<HTMLElement | null>(
      (top, o) => (!top || o.getBoundingClientRect().top < top.getBoundingClientRect().top ? o : top),
      null
    );
  }

  /**
   * Clicks `option` to commit its value, then navigates to the adjacent dropdown.
   * Captures the index BEFORE the click to survive DOM re-renders caused by
   * FormControl updates triggering change detection.
   */
  private selectAndAdvance(option: HTMLElement, fromSelect: HTMLElement, dir: 1 | -1 = 1): void {
    const idx = this.dropdownIdx(fromSelect, this.getTableDropdowns());
    option.click();
    setTimeout(() => {
      const next = this.getTableDropdowns()[idx + dir];
      if (next) {
        next.focus();
        setTimeout(() => next.click(), 0);
      }
    }, 150);
  }

  /** Moves focus + opens the next/previous table dropdown. Returns true when handled. */
  private moveTableDropdownFocus(from: HTMLElement, dir: 1 | -1): boolean {
    const all = this.getTableDropdowns();
    const nextIdx = this.dropdownIdx(from, all) + dir;
    if (nextIdx < 0 || nextIdx >= all.length) return false;
    const target = all[nextIdx];
    target.focus();
    setTimeout(() => target.click(), 0);
    return true;
  }

  /**
   * Document-level router — delegates each key to a focused handler method.
   * Must be document-level: Tab/Enter while a CDK overlay is open moves focus
   * outside the component host element.
   */
  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    const active = document.activeElement as HTMLElement;
    const inTable = !!active?.closest('#finished-product-table');
    const inOverlay = !!active?.closest('.cdk-overlay-container');

    if (event.key === 'Tab') return this.onTabKey(event, active, inTable);
    if (event.key === 'Enter' && inOverlay) return this.onEnterKey();
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'v') {
      this.onCtrlV(event, active, inTable, inOverlay);
    }
  }

  /** Tab in table (panel closed): move focus to the next/previous dropdown. */
  private onTabKey(event: KeyboardEvent, active: HTMLElement, inTable: boolean): void {
    if (!inTable) return;
    if (this.moveTableDropdownFocus(active, event.shiftKey ? -1 : 1)) event.preventDefault();
  }

  /** Enter: let Angular Material commit the highlighted option, then advance. */
  private onEnterKey(): void {
    const open = this.openTableSelect();
    if (!open) return;
    // Do NOT preventDefault — Angular Material handles the selection.
    // Poll until aria-expanded collapses, then move to the next field.
    const start = Date.now();
    const poll = (): void => {
      if (open.ariaExpanded !== 'true') {
        this.moveTableDropdownFocus(open, 1);
      } else if (Date.now() - start < 1000) {
        setTimeout(poll, 30);
      }
    };
    setTimeout(poll, 50);
  }

  /** Ctrl+V: resolve the target dropdown and delegate to handlePaste. */
  private onCtrlV(event: KeyboardEvent, active: HTMLElement, inTable: boolean, inOverlay: boolean): void {
    if (!inTable && !inOverlay) return;
    const pasteTarget = inTable
      ? active?.closest('mic-dropdown-search')?.querySelector<HTMLElement>('mat-select') ?? null
      : this.openTableSelect();
    if (!pasteTarget) return;
    event.preventDefault();
    event.stopPropagation();
    this.handlePaste(pasteTarget, inOverlay, active);
  }

  /** Opens panel (if needed), reads clipboard, then runs the async paste logic outside Angular's zone. */
  private handlePaste(matSelect: HTMLElement, panelOpen: boolean, activeEl: HTMLElement): void {
    const openPanel = (): void => matSelect.closest('mic-dropdown-search')?.querySelector<HTMLElement>('.mat-mdc-select-trigger')?.click();

    navigator.clipboard
      .readText()
      .then((raw) => {
        const text = raw.trim();
        if (!text) return;
        if (!panelOpen) openPanel();
        this.ngZone.runOutsideAngular(() => this.applyPastedText(matSelect, panelOpen, activeEl, text));
      })
      .catch(() => {
        if (!panelOpen) openPanel(); // Clipboard blocked — open panel for manual search
      });
  }

  /**
   * Runs outside Angular's zone: sets the search-input value, waits for filtering,
   * then auto-selects on exact match or unique result.
   * Multiple non-exact results: panel stays open — Tab commits topmost; Enter commits highlighted.
   */
  private async applyPastedText(matSelect: HTMLElement, panelOpen: boolean, activeEl: HTMLElement, text: string): Promise<void> {
    await new Promise<void>((r) => setTimeout(r, panelOpen ? 0 : 150));

    const searchInput: HTMLInputElement | null =
      panelOpen && activeEl.tagName === 'INPUT'
        ? (activeEl as HTMLInputElement)
        : document.querySelector<HTMLInputElement>(FinishedProductScreenComponent.SEL_SEARCH);
    if (!searchInput) return;

    // Native setter so Angular/CDK detects the value change
    Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set?.call(searchInput, text);
    searchInput.dispatchEvent(new Event('input', { bubbles: true }));

    await new Promise<void>((r) => setTimeout(r, 250));

    const opts = Array.from(document.querySelectorAll<HTMLElement>(FinishedProductScreenComponent.SEL_OPTIONS));
    if (!opts.length) return; // no match — leave panel open

    const lower = text.toLowerCase();
    const exact = opts.find((o) => o.textContent?.trim().toLowerCase() === lower) ?? null;
    // Exact match wins; unique result auto-selects; multiple non-exact → panel stays open
    const pick = exact ?? (opts.length === 1 ? opts[0] : null);
    if (pick) this.selectAndAdvance(pick, matSelect);
  }

  // ---------------------------------------------------------------------------

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.checkIfModified()) {
      $event.returnValue = true;
    }
  }

  canDeactivate(): Promise<boolean> {
    if (this.checkIfModified()) {
      return new Promise<boolean>((resolve) => {
        this.showDiscardModal();
        const modalSubscription = this.discardModal.result.subscribe((result: ModalResult) => {
          if (result && result.id === 'discardConfirmation') {
            resolve(!!result.result);
            modalSubscription.unsubscribe();
          }
        });
      });
    }
    return Promise.resolve(true);
  }

  checkIfModified(): boolean {
    const modifiedRows = [];
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    for (let i = 0; i < this.finishedProducts.length; i++) {
      const original = this.finishedProducts[i];
      const current = formArray.at(i).value;
      const isModified = this.isRowModified(original, current);
      if (isModified) {
        modifiedRows.push({ ...current, finishedProductPk: original.finishedProductPk });
      }
    }
    return modifiedRows.length > 0;
  }

  /**
   * Compares if a row has been modified, treating null, undefined, and empty string as equivalent.
   * This prevents false positives when form controls are reverted to their original values.
   */
  private isRowModified(original: FinishedProduct, current: any): boolean {
    return Object.keys(current).some((key) => {
      const originalValue = original[key];
      const currentValue = current[key];
      // Normalize empty values: null, undefined, and "" are all considered "empty"
      const normalizeEmpty = (val: any) => (val === null || val === undefined || val === '' ? 'EMPTY' : val);
      return normalizeEmpty(originalValue) !== normalizeEmpty(currentValue);
    });
  }

  // Returns the list this specific cell's dropdown should be bound to: the real list only if
  // this is the cell the user just clicked/opened, otherwise the shared empty list.
  getDropdownData(item: FinishedProduct, field: string): string[] {
    if (this.isActiveDropdownCell(item, field)) {
      return this.dealerRetreadNoList;
    }

    // Keep the selected value visible for inactive cells without binding the full list.
    const selectedValue = this.getControl(item, field as keyof FinishedProduct)?.value ?? (item as any)[field];

    return selectedValue === null || selectedValue === undefined || selectedValue === '' ? this.dealerRetreadNoEmptyList : [selectedValue];
  }

  isActiveDropdownCell(item: FinishedProduct, field: string): boolean {
    return this.activeDropdownCell?.pk === item.finishedProductPk && this.activeDropdownCell?.field === field;
  }

  // Marks this cell as the one whose dropdown was just interacted with, so its [data] binding
  // switches from the shared empty list to the real dealerRetreadNoList. Triggered on
  // (mousedown) rather than (click): mic-dropdown-search opens its panel from its own internal
  // click handler on the same click event, so by the time our (click) handler ran the panel had
  // already opened with the stale empty list — requiring a second click to show data. mousedown
  // fires earlier in the sequence (mousedown -> focus -> click -> open), so populating the data
  // here (with a synchronous detectChanges, since this component uses OnPush and markForCheck
  // alone would only schedule the update for a later cycle) guarantees the real list is already
  // bound before the panel opens.
  onDropdownCellClick(item: FinishedProduct, field: string): void {
    if (this.isActiveDropdownCell(item, field)) {
      return;
    }
    this.activeDropdownCell = { pk: item.finishedProductPk, field };
    this.cdr.detectChanges();
  }
}
